import "./ValidationMessages.scss";
import { motion } from "framer-motion";
import { useMemo, useState, useEffect } from "react";
import DOMPurify from "dompurify";
import Pagination from "@mui/material/Pagination";
import { MdClose } from "react-icons/md";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

const ValidationMessages = ({
  messageData,
  onFix,
  onClose,
  onRemind,
  onSupport,
  rowsPerPage = 3,
}) => {
  const { t } = useTranslation();
  const { currentUser } = useSelector((state) => state.user);
  const list = messageData?.result_msg_list || [];
  const issuesCount = list.length;

  const [page, setPage] = useState(1);
  const pageCount = Math.max(1, Math.ceil(list.length / rowsPerPage));

  useEffect(() => {
    setPage(1);
  }, [list.length, messageData?.result_msg, messageData?.result_status]);

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const cleanInfoHtml = useMemo(
    () => DOMPurify.sanitize(messageData?.result_msg || ""),
    [messageData?.result_msg]
  );

  const startIndex = (page - 1) * rowsPerPage;
  const paged = list.slice(startIndex, startIndex + rowsPerPage);

  const metaFor = (type = "") => {
    const t = (type || "").toLowerCase();
    if (t.includes("validation")) return { label: "VALIDATION ERROR", cls: "pill-danger" };
    if (t.includes("missing")) return { label: "MISSING INFORMATION", cls: "pill-amber" };
    if (t.includes("warning")) return { label: "WARNING", cls: "pill-amber" };
    return { label: "INFO", cls: "pill-info" };
  };

  const titleFor = (row) => {
    if (row?.msg_title) return row.msg_title;
    if (row?.msg_summary) return row.msg_summary;

    // heuristic: first sentence (short) as title
    const txt = (row?.msg_text || "").replace(/<[^>]*>/g, "").trim();
    const first = txt.split(".")[0]?.trim();
    if (first && first.length <= 60) return first;

    return row?.msg_no || "Issue";
  };

  const descFor = (row) => DOMPurify.sanitize(row?.msg_text || "");

  return (
    <motion.div
      className="vm-shell"
      initial={{ opacity: 0, scale: 0.985 }}
      animate={{ opacity: 1, scale: 1, transition: { duration: 0.22 } }}
      exit={{ opacity: 0, scale: 0.985, transition: { duration: 0.18 } }}
    >
      {/* HEADER */}
      <div className="vm-header">
        <div className="vm-header-left">
          <div className="vm-alert-icon" aria-hidden="true">!</div>
          <div className="vm-header-copy">
            <div className="vm-title">
              {issuesCount} {issuesCount === 1 ? "Issue" : "Issues"} Found
            </div>
            <div className="vm-subtitle">
              Please resolve these exceptions to proceed.
            </div>
          </div>
        </div>

        <button className="vm-close" onClick={onClose} aria-label="Close">
          <MdClose />
        </button>
      </div>

      {/* INFO BANNER */}
      {!!cleanInfoHtml && (
        <div className="vm-info">
          <div className="vm-info-icon" aria-hidden="true">
            <AiOutlineInfoCircle />
          </div>
          <div className="vm-info-text" dangerouslySetInnerHTML={{ __html: cleanInfoHtml }} />
        </div>
      )}

      {/* ISSUES LIST */}
      <div className="vm-list">
        {paged.map((row) => {
          const meta = metaFor(row?.msg_type);
          const code = row?.msg_no ? `#${row.msg_no}` : "";

          return (
            <div key={row?.msg_index} className="vm-card">
              <div className="vm-card-main">
                <div className="vm-card-top">
                  <span className={`vm-pill ${meta.cls}`}>{meta.label}</span>
                  {code && <span className="vm-code">{code}</span>}
                </div>

                <div className="vm-card-title">{titleFor(row)}</div>

                <div
                  className="vm-card-desc"
                  dangerouslySetInnerHTML={{ __html: descFor(row) }}
                />
              </div>

              {/* <button
                type="button"
                className="vm-fix"
                onClick={() => onFix?.(row)}
                disabled={!onFix}
                title={!onFix ? "No fix handler provided" : "Fix Now"}
              >
                Fix Now <span className="vm-arrow" aria-hidden="true">→</span>
              </button> */}
            </div>
          );
        })}

        {list.length > rowsPerPage && (
          <div className="vm-pager">
            <Pagination
              count={pageCount}
              page={page}
              onChange={(e, p) => setPage(p)}
              shape="rounded"
              siblingCount={1}
              boundaryCount={1}
            />
            <span className="vm-pager-meta">
              Showing {startIndex + 1}–{Math.min(startIndex + rowsPerPage, list.length)} of {list.length}
            </span>
          </div>
        )}
      </div>

      {/* FOOTER (like screenshot) */}
      <div className="vm-footer">
        {["agent", "broker"].includes(currentUser?.token_org_type) ?
          <span />
          :
          <button type="button" className="vm-support" onClick={onSupport}>
            {t('needHelp')} <span className="vm-support-link">{t('contactSupport')}</span>
          </button>}

        <div className="vm-footer-actions">
          {/* <button type="button" className="vm-remind" onClick={onRemind}>
            Remind Me Later
          </button> */}
          <button type="button" className="vm-closeChecklist" onClick={onClose}>
            {t('closeChecklist')}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default ValidationMessages;
